/**
 * Created by Vladislav on 29.01.2025.
 */

const Metrics = function () {
    this.batch = undefined;
    this.batches = [];

    this.flushThrottled = connector.utils.timeredThrottle(connector.utils.parseInterval("10 seconds"), () => this.flush());

    this.addBatch = connector.utils.accumulate(0, () => this._addBatch());
    this.sendEvents = connector.utils.accumulate(0, () => this._sendEvents());

    this.start = connector.utils.once(() => this._start());

    this.bus = new ConnectorEventEmitter();
};

Metrics.prototype._start = function () {
    const isNewUser = this.visited === this.registered;

    if (isNewUser) {
        this.onStartDay();
        this.onStartSession();
    }

    this._onActivity();

    connector.platform.on("userIdChanged", ({ oldId }) => this.onUserIdChanged(oldId));

    connector.platform.on("pause", () => {
        this._accumulateActiveTime();
        this.lastTick = undefined;

        this.save();
        this._sendEventsOnPause();
    });

    this.lastTick = connector.platform.isPaused ? undefined : Date.now();
    connector.timeouts.setInterval(() => this._accumulateActiveTime(), connector.utils.parseInterval(Metrics.ACTIVE_TIME_TICK));

    connector.timeouts.setInterval(() => this.flushThrottled(), connector.utils.parseInterval(Metrics.FLUSH_INTERVAL));
    this.flushThrottled();

    connector.timeouts.setInterval(() => this.sendEvents(), connector.utils.parseInterval(Metrics.SEND_EVENTS_INTERVAL));
    this.sendEvents();

    for (const minutes of [5, 10, 15, 30, 60]) {
        connector.timeouts.setTimeout(() => {
            connector.conversions.trackEvent(connector.EVENTS.CONVERSIONS.SESSION_TIME + minutes);
        }, minutes * 60000);
    }
};

Metrics.prototype.load = function (isNewUser) {
    let data = connector.dataLoader.load(SimpleDataLoader.TYPES.METRICS);
    const now = Date.now();
    if (isNewUser) {
        data = {
            registered: now,
            visited: now,
            session: 0
        };
    } else if (!data) {
        data = {
            registered: Date.now() - 1000 * 60 * 60 * 24 * 31,
            visited: Date.now() - 1000 * 60 * 60 * 24
        };
    }
    if (data.session === undefined) {
        data.session = 10;
    }

    this.registered = data.registered;
    this.visited = data.visited;
    this.session = data.session;
    this.sessionStart = data.sessionStart || now;
    this.activeTime = data.timeSpent || data.activeTime || 0;
    this.activeMs = data.activeMs || 0;
    this.lastInput = now;
    this.lastTick = now;
    this.channel = data.channel || "";
    this.baseParameters = data.baseParameters || {};
    this.lastReportMau = data.lastReportMau || 0;
    this.lastReportDeviceId = data.lastReportDeviceId;
    this.previousUserId = data.previousUserId;
    this.batches = data.batches || [];

    // backward compatibility 07.07.26
    const events = data.events || {};

    for (const [key, params] of Object.entries(events)) {
        this.logEvent(key, params);
    }
};

Metrics.prototype.save = function () {
    connector.dataLoader.save(SimpleDataLoader.TYPES.METRICS, {
        registered: this.registered,
        visited: this.visited,
        session: this.session,
        activeTime: this.activeTime,
        activeMs: this.activeMs,
        sessionStart: this.sessionStart,
        channel: this.channel,
        baseParameters: this.baseParameters || {},
        lastReportMau: this.lastReportMau,
        lastReportDeviceId: this.lastReportDeviceId,
        previousUserId: this.previousUserId,
        batches: this.batches
    });
};

Metrics.prototype.on = function (eventName, callback) {
    return this.bus.on(eventName, callback);
};

Metrics.prototype.setRegistered = function (registered) {
    this.registered = registered;

    if (this.getDaysSinceRegistration() > 10) {
        this.session = Math.max(this.session, 10);
    }

    this.save();
};

Metrics.prototype.setVisited = function (visited) {
    if (this.visited !== visited) {
        this.sessionStart = Date.now();
    }

    this.visited = visited;

    this.save();
};

Metrics.prototype.setChannel = function (channel) {
    this.channel = channel;

    this.save();
};

Metrics.prototype._onActivity = function () {
    const now = Date.now();

    const isSameDay = this.getDaysSinceRegistration(this.visited) === this.getDaysSinceRegistration();
    const isSameSession = now - this.visited < connector.utils.parseInterval(Metrics.SESSION_TIMEOUT);

    if (!isSameDay || !isSameSession) {
        this.flush();
    }

    if (!isSameDay) {
        this.onFinishDay(this.visited);
        this.onStartDay();
    }

    if (!isSameSession) {
        this.onFinishSession();
        this.onStartSession();
    }

    this.visited = now;
    this.bus.trigger("updateVisited", this.visited);

    this.save();
};

Metrics.prototype.registerInput = function () {
    this.lastInput = Date.now();
};

Metrics.prototype._accumulateActiveTime = function () {
    const now = Date.now();
    const lastTick = this.lastTick;

    this.lastTick = now;

    if (lastTick === undefined) {
        return;
    }

    const delta = now - lastTick;

    if (delta <= 0 || delta > connector.utils.parseInterval(Metrics.MAX_ENGAGED_CHUNK)) {
        return;
    }

    if (now - this.lastInput > connector.utils.parseInterval(Metrics.AFK_TIMEOUT)) {
        return;
    }

    this.activeMs += delta;

    const minutes = Math.floor(this.activeMs / 60000);
    if (minutes > 0) {
        this.activeMs -= minutes * 60000;
        this._addActiveTime(minutes);
    }
};

Metrics.prototype._addActiveTime = function (minutes) {
    this._onActivity();

    this.activeTime += minutes;
    this.bus.trigger("updateTimeIncrement");

    this.save();
};

Metrics.prototype.flush = function () {
    const dayStart = this.getDayStart(this.visited);
    if (this.activeTime) {
        this.logEvent(connector.EVENTS.STATS.ACTIVE_TIME, { value: this.activeTime, event: dayStart });
        this.logEvent(connector.EVENTS.STATS.SESSION_TIME, { value: this.activeTime, event: this.sessionStart });
    }

    this.bus.trigger("flush", {
        sessionStart: this.sessionStart,
        session: this.session,
        dayStart,
        activeTime: this.activeTime
    });

    this.activeTime = 0;

    this.save();
};

Metrics.prototype.onFinishDay = function (date) {
    this.bus.trigger("finishDay", {
        date: this.getDayStart(date)
    });
};

Metrics.prototype.onStartDay = function () {
    const daysSinceRegistration = this.getDaysSinceRegistration();

    this.logEvent(connector.EVENTS.STATS.DAU);
    this.logEvent(connector.EVENTS.STATS.CONNECTOR_VERSION + connector.version);

    if ((this.lastReportMau || 0) + connector.utils.parseInterval("30 day") < Date.now()) {
        this.logEvent(connector.EVENTS.STATS.MAU);
        this.lastReportMau = Date.now();
    }

    if (connector.info.deviceID && (daysSinceRegistration % 10 === 0 || this.lastReportDeviceId !== connector.info.deviceID)) {
        this.logEvent(connector.EVENTS.STATS.UPDATE_DEVICE_ID, { itemId: connector.info.deviceID });
        this.lastReportDeviceId = connector.info.deviceID;
    }

    if (this.previousUserId && (daysSinceRegistration % 10 === 0 || daysSinceRegistration < 28)) {
        this.logEvent(connector.EVENTS.STATS.UPDATE_USER_ID, { itemId: this.previousUserId });
    }

    this.save();

    this.logEvent(`${connector.EVENTS.STATS.ACTIVE_TIME}_count`, { event: this.getDayStart() });

    if (daysSinceRegistration === 0) {
        this.logEvent(connector.EVENTS.STATS.REGISTERED, { conversion: true });

        this.logEvent(connector.EVENTS.STATS.TRANSIT, { conversion: true });

        connector.timeouts.setTimeout(() => {
            this.logEvent(connector.EVENTS.STATS.TRANSIT, { value: -1, conversion: true });
        }, connector.utils.parseInterval("5 minutes"));

        connector._predictionManager.logDay1Dau();
    }

    const retentionEvent = connector.EVENTS.STATS[`RETENTION_${daysSinceRegistration}`];
    if (retentionEvent) {
        this.logEvent(retentionEvent, {
            event: this.registered,
            conversion: true
        });
    }

    connector.conversions.trackEvent(connector.EVENTS.CONVERSIONS.VISIT, {
        nickname: "d180a_visit,d7a_visit"
    });

    this.bus.trigger("startDay");
};

Metrics.prototype.onFinishSession = function () {
    this.bus.trigger("finishSession", {
        session: this.session
    });
    this.session++;
};

Metrics.prototype.onStartSession = function () {
    this.sessionStart = Date.now();

    this.logEvent(`${connector.EVENTS.STATS.SESSION_TIME}_count`, { event: this.sessionStart });

    this.bus.trigger("startSession", {
        session: this.session
    });
};

Metrics.prototype.onUserIdChanged = function (previousUserId) {
    if (!previousUserId) {
        return;
    }

    this.previousUserId = previousUserId;

    this.logEvent(connector.EVENTS.STATS.UPDATE_USER_ID, { itemId: this.previousUserId });
    this.save();
};

Metrics.prototype.getDaysSinceRegistration = function (timestamp) {
    return Math.floor(((timestamp || Date.now()) - this.registered) / 1000 / 24 / 60 / 60);
};

Metrics.prototype.getDayStart = function (timestamp) {
    const daysSinceRegistration = this.getDaysSinceRegistration(timestamp);
    return this.registered + connector.utils.parseInterval("1 day") * daysSinceRegistration;
};

Metrics.prototype.logEvent = function (name, params = {}) {
    if (!connector.config.metricsApiURL) {
        return;
    }

    if (connector.config.norest) {
        return;
    }

    // Google Play's pre-launch robots are not players
    if (connector.info.isGoogleTestDevice) {
        return;
    }

    if (name.length > 50) {
        console.error(`[connector] Event name length is more than 50 characters: ${name}`);

        name = name.substring(0, 50);
    }

    this.batch = this.batch || { names: [], params: [] };

    this.batch.names.push(name);
    this.batch.params.push(params);

    this.addBatch();

    if (Metrics.IMPORTANT_EVENTS.includes(name) || this.session === 0) {
        this.sendEvents();
    }
};

Metrics.prototype._addBatch = function () {
    const batch = this.batch;
    if (!batch) {
        return;
    }
    this.batch = undefined;

    const events = batch.names.join(",");
    console.log(`Sending events - ${events}`);

    const data = this.getBaseParameters();
    data.events = events;
    data.params = batch.params;
    data.event = Date.now();

    this.batches.push(data);
    this.save();

    const eventsAmount = this.batches.reduce((acc, b) => acc + b.params.length, 0);
    if (eventsAmount >= 100) {
        this.sendEvents();
    }
};

Metrics.prototype._sendEvents = function () {
    const batches = this.batches;

    if (batches.length === 0) {
        return;
    }

    ConnectorRestClient.post(
        `${connector.config.metricsApiURL}/events/`,
        { batches },
        () => {},
        () => {
            console.log("[connector] Failed to send metrics", batches);
        }, 
        {}
    );

    this.batches = [];
    this.save();
};

Metrics.prototype._sendEventsOnPause = function () {
    this._addBatch();

    if (this.batches.length > 0 && connector.utils.postOnPause(`${connector.config.metricsApiURL}/events/?pause=1`, { batches: this.batches })) {
        this.batches = [];
        this.save();
    }
};

Metrics.prototype.getBaseParameters = function () {
    return {
        ...this.baseParameters,
        version: connector.config.appVersion || this.baseParameters.version,
        id: connector.platform.getUserID(),
        source: connector.info.source,
        registered: this.registered,
        channel: this.channel,
        language: connector.info.language,
        platform: connector.info.platform
    };
};

Metrics.prototype.setBaseParameters = function (baseParameters) {
    this.baseParameters = baseParameters;
    this.save();
};

Metrics.SESSION_TIMEOUT = "1 hour";
Metrics.FLUSH_INTERVAL = "5 minutes";
Metrics.SEND_EVENTS_INTERVAL = "1 minute";

Metrics.ACTIVE_TIME_TICK = "10 seconds";
Metrics.MAX_ENGAGED_CHUNK = "1 minute";
Metrics.AFK_TIMEOUT = "3 minutes";

Metrics.IMPORTANT_EVENTS = [
    EVENTS.STATS.PAYMENTS
];
